import {
  DefinitionNode,
  DocumentNode,
  EnumValueDefinitionNode,
  FieldDefinitionNode,
  InputValueDefinitionNode,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';

export const HIDDEN_SECTION_HEIGHT_IN_LINES = 2;

function getRangeForTypeDef(
  node:
    | DefinitionNode
    | FieldDefinitionNode
    | InputValueDefinitionNode
    | EnumValueDefinitionNode,
  includeDescription: boolean,
) {
  const startLine =
    'description' in node && node.description
      ? includeDescription
        ? node.description?.loc?.startToken.line
        : node.loc?.startToken?.next?.line // 'next' will get the 'name' node next to the description
      : node.loc?.startToken.line;
  const endLine = node.loc?.endToken.line;
  if (!startLine || !endLine) {
    return null;
  }

  return { startLine, endLine };
}

function lineNumInRange(
  lineNumber: number,
  range: { startLine: number; endLine: number } | null,
) {
  return range && range.startLine <= lineNumber && lineNumber <= range.endLine;
}

/**
 * given a line number, get the value of the first line, excluding descriptions
 */
export function getFirstLineOfParentTypeAtLineNumber(
  typeDefs: DocumentNode,
  lineNumber: number,
  model: monacoEditor.editor.IModel | null,
): string | undefined {
  if (
    !model ||
    // the model line count will be 1 before the modal has been populated
    model.getLineCount() < lineNumber
  )
    return undefined;
  let result: string | undefined;
  const typeDefAtLineNumber = typeDefs.definitions.find((typeDefinition) => {
    return lineNumInRange(lineNumber, getRangeForTypeDef(typeDefinition, true));
  });
  if (!typeDefAtLineNumber) return;
  const typeDefStartLine = getRangeForTypeDef(
    typeDefAtLineNumber,
    false,
  )?.startLine;
  if (typeDefStartLine) result = model.getLineContent(typeDefStartLine);

  return result;
}

const LINES_BETWEEN_HIDDEN_INDICATORS = 5;
export function getRangesToShow({
  lineChanges,
  diffEditorSide,
  model,
  accountForHiddenSectionIndicator,
}: {
  lineChanges: monacoEditor.editor.ILineChange[];
  diffEditorSide: 'modified' | 'original';
  model: monacoEditor.editor.ITextModel;
  // we add absolutely positioned indicators above changes,
  // and they are HIDDEN_SECTION_NUM_LINES_COVERED lines tall,
  // so we show HIDDEN_SECTION_NUM_LINES_COVERED additional lines for them to lay on top of
  accountForHiddenSectionIndicator: boolean;
}) {
  const totalLineCount = model.getLineCount();
  return lineChanges.map((change) => {
    const startLineNumber =
      diffEditorSide === 'modified'
        ? change.modifiedStartLineNumber
        : change.originalStartLineNumber;
    const endLineNumber =
      diffEditorSide === 'modified'
        ? change.modifiedEndLineNumber
        : change.originalEndLineNumber;

    // include 5 lines above and below the change
    const rangeToInclude = {
      startLine: Math.max(
        1,
        startLineNumber -
          LINES_BETWEEN_HIDDEN_INDICATORS +
          // if the endLineNumber is 0, this is a change only in the opposite diff side
          // (either only a removal or only an addition) & the start & end are the same line number,
          // so shift forward one in the start line to account for that
          (endLineNumber === 0 ? 1 : 0),
      ),
      endLine: Math.min(
        totalLineCount,
        // if the endLineNumber is 0, this is a change only in the opposite diff side
        (endLineNumber === 0 ? startLineNumber : endLineNumber) +
          LINES_BETWEEN_HIDDEN_INDICATORS +
          (accountForHiddenSectionIndicator
            ? HIDDEN_SECTION_HEIGHT_IN_LINES
            : 0),
      ),
    };

    return new monacoEditor.Range(
      rangeToInclude.startLine,
      0,
      rangeToInclude.endLine,
      0,
    );
  }, [] as monacoEditor.Range[]);
}
